/**
 * EPBCS Phase 3 — CapEx & Depreciation section.
 * Tabs: WBS Projects, Capital Planning, Non-Project, Valid Combinations, Review.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash, CaretDown, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, Input, Select, Btn } from "./common";
import { ActionButton } from "./WorkflowActions";
import { PovBar, useDims, dimOptions, fmtMonth, fmtNum, ScenarioTh } from "./povUtils";
import { PlanningTab, AssetsTab, CombosTab, ReviewTab } from "./CapexAnalysis";
import { ItemComments, useCommentCounts } from "./ItemComments";

const CAPEX_POV_KEYS = ["entity", "line_of_business", "department", "version"];

export function useCapexPov(dims) {
  const [pov, setPov] = useState(null);
  useEffect(() => {
    if (dims && !pov) {
      const first = (t) => dims[t]?.[0]?.code || "";
      setPov({ entity: first("entity"), line_of_business: first("line_of_business"),
        department: first("department"), version: first("version") });
    }
  }, [dims]); // eslint-disable-line
  return [pov, setPov];
}

const POV_LABELS = { entity: "Entity", division: "Division", line_of_business: "Line of Business",
  department: "Department", currency: "Currency", version: "Version" };

export const capexPovFields = (dims) => CAPEX_POV_KEYS.map((k) => ({
  k, label: POV_LABELS[k] || (k.charAt(0).toUpperCase() + k.slice(1)), options: dimOptions(dims, k),
}));

export default function CapexSection({ sub }) {
  const raw = sub === "assets" ? "assets_new" : sub;
  const tab = ["wbs_new", "wbs_existing", "planning", "assets_new", "assets_existing", "combos", "review"]
    .includes(raw) ? raw : "wbs_new";
  return (
    <div data-testid="epbcs-capex-section">
      {tab === "wbs_new" && <WbsTab mode="new" />}
      {tab === "wbs_existing" && <WbsTab mode="existing" />}
      {tab === "planning" && <PlanningTab />}
      {tab === "assets_new" && <AssetsTab mode="new" />}
      {tab === "assets_existing" && <AssetsTab mode="existing" />}
      {tab === "combos" && <CombosTab />}
      {tab === "review" && <ReviewTab />}
    </div>
  );
}

// ------------------------------------------------------------ WBS tab ------
const EMPTY_TASK = {
  task_name: "", description: "", supplier: "", start_date: "", end_date: "",
  asset_category: "", currency: "", spread_method: "even", total_budget: "",
};

const WbsTab = ({ mode }) => {
  const isNew = mode === "new";
  const dims = useDims();
  const [pov, setPov] = useCapexPov(dims);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [combos, setCombos] = useState([]);
  const [selected, setSelected] = useState([]);
  const commentCounts = useCommentCounts("capex", pov);
  const load = () => {
    if (!pov) return;
    setError(null);
    api.post("/epbcs/projects/list", { pov })
      .then((r) => { setData(r.data); setSelected([]); })
      .catch((e) => { setData(null); setError(e?.response?.data?.detail || e.message); });
  };
  useEffect(load, [pov]); // eslint-disable-line
  useEffect(() => { api.get("/epbcs/valid-combos").then((r) => setCombos(r.data)).catch(() => {}); }, []);

  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="wbs-error">{error}</p>;
  const canEdit = data && (data.perm === "input" || data.perm === "full");
  const canApprove = data && data.perm === "full";
  const visibleProjects = (data?.projects || []).filter((p) => Boolean(p.is_existing) !== isNew);

  const createProject = async () => {
    try {
      await api.post("/epbcs/projects", { pov, ...draft });
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create project");
    }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulkStatus = async (status) => {
    try {
      await api.post("/epbcs/projects/bulk-status", { project_ids: selected, status });
      toast.success(status === "approved" ? "Selected projects approved" : "Selected projects put on hold");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update projects");
    }
  };

  return (
    <div className="space-y-4" data-testid={`capex-wbs-tab-${mode}`}>
      <p className="text-[11px] text-slate-500 -mb-1">
        {isNew
          ? "Brand-new projects — nothing has started yet (no task has an actual/past start date)."
          : "Projects that have already commenced — project name & code are locked; add, edit or budget their tasks below."}
      </p>
      <PovBar testid={`pov-capex_wbs_${mode}`} pov={pov} setPov={setPov} fields={capexPovFields(dims)}
        extra={<ActionButton section="capex" pov={pov} onChanged={load} />} />
      <div className="flex flex-wrap items-center gap-2">
        {isNew && canEdit && !draft && (
          <button onClick={() => setDraft({ project_name: "", division: dims.division?.[0]?.code || "", description: "" })}
            data-testid="new-project-btn"
            className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
            <Plus size={13} weight="bold" /> New project
          </button>
        )}
        {canApprove && selected.length > 0 && (
          <div className="flex items-center gap-2 ml-auto" data-testid="wbs-bulk-toolbar">
            <span className="text-[11px] font-semibold text-slate-500">{selected.length} selected</span>
            <button onClick={() => bulkStatus("approved")} data-testid="wbs-bulk-approve"
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">
              <CheckIcon /> Approve selected
            </button>
            <button onClick={() => bulkStatus("on_hold")} data-testid="wbs-bulk-hold"
              className="inline-flex items-center gap-1.5 border border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">
              On hold
            </button>
          </div>
        )}
      </div>
      {isNew && draft && (
        <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4" data-testid="project-draft">
          <h4 className="font-bold text-sm mb-3">New project <span className="text-[11px] font-normal text-slate-500">(code auto-generated: Division_FYyy_Seq)</span></h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Project Name *"><Input data-testid="project-name" value={draft.project_name}
              onChange={(e) => setDraft({ ...draft, project_name: e.target.value })} /></Field>
            <Field label="Division *">
              <Select value={draft.division} data-testid="project-cc"
                onChange={(e) => setDraft({ ...draft, division: e.target.value })}>
                {(dims.division || []).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </Select>
            </Field>
            <Field label="Description"><Input value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
            <Btn onClick={createProject} disabled={!draft.project_name || !draft.division} data-testid="project-create">Create project</Btn>
          </div>
        </div>
      )}
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : visibleProjects.length === 0 ? (
        <p className="text-sm text-slate-400 rounded-sm border border-slate-200 bg-white p-6 text-center" data-testid="wbs-empty">
          {isNew ? `No new projects for this POV yet.${canEdit ? " Create one above." : ""}`
                 : "No existing (already-commenced) projects for this POV yet."}
        </p>
      ) : visibleProjects.map((p) => (
        <ProjectCard key={p.id} project={p} months={data.months} scenarioByMonth={data.scenario_by_month} dims={dims} canEdit={canEdit} readOnlyHeader={!isNew}
          canApprove={canApprove} selected={selected.includes(p.id)} onToggleSelect={() => toggleSelect(p.id)}
          combos={combos} onChanged={load} pov={pov} commentCounts={commentCounts} />
      ))}
    </div>
  );
};

const CheckIcon = () => <span aria-hidden="true">✓</span>;

const WBS_STATUS_STYLE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200",
};
const WBS_STATUS_LABEL = { approved: "Approved", on_hold: "On Hold" };

const ProjectCard = ({ project, months, scenarioByMonth, dims, canEdit, canApprove, selected, onToggleSelect, combos, onChanged, pov, commentCounts, readOnlyHeader }) => {
  const [open, setOpen] = useState(true);
  const [taskDraft, setTaskDraft] = useState(null);
  const [projEdit, setProjEdit] = useState(null);
  const total = project.tasks.reduce((s, t) => s + (t.total_budget || 0), 0);
  const validCats = (dims.asset_category || []).filter((c) =>
    combos.some((v) => v.division === project.division && v.asset_category === c.code));

  const saveProject = async () => {
    try {
      await api.post("/epbcs/projects", { id: project.id, ...projEdit });
      toast.success("Project updated");
      setProjEdit(null);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update project");
    }
  };

  const saveTask = async (t) => {
    try {
      await api.post(`/epbcs/projects/${project.id}/tasks`, { ...t, total_budget: +t.total_budget || 0 });
      toast.success("Task saved");
      setTaskDraft(null);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save task");
    }
  };

  const taskMonths = taskDraft
    ? months.filter((m) => (!taskDraft.start_date || m >= taskDraft.start_date.slice(0, 7)) &&
                           (!taskDraft.end_date || m <= taskDraft.end_date.slice(0, 7)))
    : [];
  const manualTotal = taskDraft
    ? taskMonths.reduce((s, m) => s + (+taskDraft.allocations?.[m] || 0), 0)
    : 0;
  const isManual = taskDraft?.spread_method === "manual";

  return (
    <div className="rounded-sm border border-slate-200 bg-white shadow-sm" data-testid={`project-card-${project.project_code}`}>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 cursor-pointer" onClick={() => setOpen(!open)}>
        {canApprove && (
          <input type="checkbox" checked={selected} onClick={(e) => e.stopPropagation()}
            onChange={onToggleSelect} data-testid={`wbs-select-${project.project_code}`} className="accent-[#0B1E3F]" />
        )}
        <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "" : "-rotate-90"}`} />
        <span className="font-mono font-bold text-[#0B1E3F]" data-testid={`project-code-${project.project_code}`}>{project.project_code}</span>
        <span className="font-semibold">{project.project_name}</span>
        <span className="text-[11px] text-slate-500">Division: {project.division} · {project.tasks.length} task(s)</span>
        {project.wbs_status && (
          <span data-testid={`wbs-status-${project.project_code}`}
            className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${WBS_STATUS_STYLE[project.wbs_status] || ""}`}>
            {WBS_STATUS_LABEL[project.wbs_status] || project.wbs_status}
          </span>
        )}
        {readOnlyHeader && (
          <span data-testid={`wbs-locked-${project.project_code}`}
            className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border border-slate-300 bg-slate-100 text-slate-600">
            Existing — locked
          </span>
        )}
        <span className="ml-auto font-bold text-sm">{fmtNum(total)}</span>
        <ItemComments section="capex" pov={pov} itemType="project"
          itemKey={project.project_code} itemLabel={`${project.project_code} — ${project.project_name}`}
          count={commentCounts.counts[project.project_code] || 0} onChanged={commentCounts.refresh} />
        {canEdit && !readOnlyHeader && (
          <>
            <button onClick={(e) => { e.stopPropagation(); setProjEdit({ project_name: project.project_name, description: project.description || "" }); }}
              data-testid={`project-edit-${project.project_code}`} title="Edit project"
              className="text-slate-400 hover:text-[#0B1E3F]"><PencilSimple size={15} /></button>
            <button onClick={async (e) => { e.stopPropagation(); await api.delete(`/epbcs/projects/${project.id}`); onChanged(); }}
              data-testid={`project-delete-${project.project_code}`}
              className="text-slate-400 hover:text-red-600"><Trash size={15} /></button>
          </>
        )}
      </div>
      {projEdit && (
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-end gap-3" data-testid={`project-edit-form-${project.project_code}`}>
          <Field label="Project Name *"><Input data-testid="project-edit-name" value={projEdit.project_name}
            onChange={(e) => setProjEdit({ ...projEdit, project_name: e.target.value })} /></Field>
          <Field label="Description"><Input value={projEdit.description}
            onChange={(e) => setProjEdit({ ...projEdit, description: e.target.value })} /></Field>
          <div className="flex gap-2 pb-0.5">
            <button onClick={() => setProjEdit(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded">Cancel</button>
            <Btn onClick={saveProject} disabled={!projEdit.project_name} data-testid="project-edit-save">Save</Btn>
          </div>
        </div>
      )}
      {open && (
        <div className="p-4 space-y-3">
          {project.tasks.length > 0 && (
            <div className="overflow-x-auto rounded border border-slate-100">
              <table className="text-[12px] w-full">
                <thead>
                  <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="py-2 px-2">Task #</th><th className="py-2 px-2 min-w-[130px]">Task</th>
                    <th className="py-2 px-2">Supplier</th><th className="py-2 px-2">Start</th><th className="py-2 px-2">End</th>
                    <th className="py-2 px-2">Category</th><th className="py-2 px-2">Life</th><th className="py-2 px-2">Ccy</th>
                    <th className="py-2 px-2">Spread</th><th className="py-2 px-2 text-right">Budget</th>
                    {months.map((m) => <ScenarioTh key={m} m={m} scenarioByMonth={scenarioByMonth} testid={`scenario-badge-wbs-${m}`} />)}
                    {canEdit && <th className="w-8" />}
                  </tr>
                </thead>
                <tbody>
                  {project.tasks.map((t) => (
                    <tr key={t.task_no} className="border-b border-slate-100" data-testid={`task-row-${project.project_code}-${t.task_no}`}>
                      <td className="py-1.5 px-2 font-mono">{t.task_no}
                        <span className="ml-1.5"><ItemComments section="capex" pov={pov} itemType="task"
                          itemKey={`${project.project_code}#T${t.task_no}`}
                          itemLabel={`${project.project_code} · Task ${t.task_no} — ${t.task_name}`}
                          count={commentCounts.counts[`${project.project_code}#T${t.task_no}`] || 0}
                          onChanged={commentCounts.refresh} /></span>
                      </td>
                      <td className="py-1.5 px-2 font-medium whitespace-nowrap">{t.task_name}</td>
                      <td className="py-1.5 px-2">{t.supplier}</td>
                      <td className="py-1.5 px-2 whitespace-nowrap">{t.start_date}</td>
                      <td className="py-1.5 px-2 whitespace-nowrap">{t.end_date}</td>
                      <td className="py-1.5 px-2">{t.asset_category}</td>
                      <td className="py-1.5 px-2">{t.useful_life_months}m</td>
                      <td className="py-1.5 px-2">{t.currency}</td>
                      <td className="py-1.5 px-2">{t.spread_method}</td>
                      <td className="py-1.5 px-2 text-right font-semibold">{fmtNum(t.total_budget)}</td>
                      {months.map((m) => (
                        <td key={m} className={`py-1.5 px-2 text-right whitespace-nowrap ${t.allocations?.[m] ? "text-[#0B1E3F] font-medium" : "text-slate-300"}`}>
                          {fmtNum(t.allocations?.[m] || 0)}
                        </td>
                      ))}
                      {canEdit && (
                        <td className="py-1.5 px-2 whitespace-nowrap">
                          <button onClick={() => setTaskDraft({ ...EMPTY_TASK, ...t, allocations: { ...(t.allocations || {}) } })}
                            data-testid={`task-edit-${project.project_code}-${t.task_no}`} title="Edit task"
                            className="mr-1.5 text-slate-400 hover:text-[#0B1E3F]"><PencilSimple size={13} /></button>
                          <button onClick={async () => { await api.delete(`/epbcs/projects/${project.id}/tasks/${t.task_no}`); onChanged(); }}
                            data-testid={`task-delete-${project.project_code}-${t.task_no}`}
                            className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {canEdit && !taskDraft && (
            validCats.length === 0 ? (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded"
                data-testid={`no-combos-${project.project_code}`}>
                No asset categories are assigned to division {project.division} — configure Valid
                Combinations before adding tasks.
              </p>
            ) : (
              <button onClick={() => setTaskDraft({ ...EMPTY_TASK, currency: dims.currency?.[0]?.code || "",
                  asset_category: validCats[0]?.code || "", allocations: {} })}
                data-testid={`add-task-btn-${project.project_code}`}
                className="inline-flex items-center gap-1 border border-[#0B1E3F] text-[#0B1E3F] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded hover:bg-slate-50">
                <Plus size={11} weight="bold" /> Add task
              </button>
            )
          )}
          {taskDraft && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`task-draft-${project.project_code}`}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Field label="Task Name"><Input data-testid="task-name" value={taskDraft.task_name}
                  onChange={(e) => setTaskDraft({ ...taskDraft, task_name: e.target.value })} /></Field>
                <Field label="Supplier"><Input value={taskDraft.supplier}
                  onChange={(e) => setTaskDraft({ ...taskDraft, supplier: e.target.value })} /></Field>
                <Field label="Start Date"><Input type="date" data-testid="task-start" value={taskDraft.start_date}
                  onChange={(e) => setTaskDraft({ ...taskDraft, start_date: e.target.value })} /></Field>
                <Field label="End Date"><Input type="date" data-testid="task-end" value={taskDraft.end_date}
                  onChange={(e) => setTaskDraft({ ...taskDraft, end_date: e.target.value })} /></Field>
                <Field label="Asset Category (valid for this CC)">
                  <Select value={taskDraft.asset_category} data-testid="task-category"
                    onChange={(e) => setTaskDraft({ ...taskDraft, asset_category: e.target.value })}>
                    {validCats.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                  </Select>
                </Field>
                <Field label="Currency *">
                  <Select value={taskDraft.currency} onChange={(e) => setTaskDraft({ ...taskDraft, currency: e.target.value })}>
                    {(dims.currency || []).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                  </Select>
                </Field>
                <Field label="Spread Method *">
                  <Select value={taskDraft.spread_method} data-testid="task-spread"
                    onChange={(e) => setTaskDraft({ ...taskDraft, spread_method: e.target.value })}>
                    <option value="even">Even Spread</option><option value="manual">Manual Spread</option>
                  </Select>
                </Field>
                <Field label={isManual ? "Total Budget * (= sum of months)" : "Total Budget *"}>
                  <Input type="number" min="0" data-testid="task-budget"
                    value={isManual ? manualTotal : taskDraft.total_budget}
                    disabled={isManual}
                    className={isManual ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}
                    onChange={(e) => setTaskDraft({ ...taskDraft, total_budget: e.target.value })} />
                </Field>
                <Field label="Description"><Input value={taskDraft.description}
                  onChange={(e) => setTaskDraft({ ...taskDraft, description: e.target.value })} /></Field>
              </div>
              {isManual && (
                <div className="mt-3">
                  <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 mb-1.5">
                    Monthly amounts — only months within the task period are open
                  </div>
                  {(!taskDraft.start_date || !taskDraft.end_date) ? (
                    <p className="text-[11px] text-amber-700" data-testid="manual-needs-dates">
                      Set the task start and end dates first.
                    </p>
                  ) : taskMonths.length === 0 ? (
                    <p className="text-[11px] text-amber-700">The task period falls outside the budget window.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="manual-months">
                      {taskMonths.map((m) => (
                        <div key={m} className="w-[86px]">
                          <div className="text-[9px] uppercase font-bold text-slate-400 text-center">{fmtMonth(m)}</div>
                          <Input type="number" min="0" data-testid={`task-alloc-${m}`}
                            value={taskDraft.allocations?.[m] ?? ""}
                            onChange={(e) => setTaskDraft({ ...taskDraft,
                              allocations: { ...taskDraft.allocations, [m]: e.target.value } })} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3 flex gap-2 justify-end">
                <button onClick={() => setTaskDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded">Cancel</button>
                <Btn onClick={() => saveTask(isManual ? { ...taskDraft, total_budget: manualTotal } : taskDraft)}
                  data-testid="task-save"
                  disabled={!taskDraft.task_name || !taskDraft.start_date || !taskDraft.end_date ||
                    (isManual ? manualTotal <= 0 : !taskDraft.total_budget)}>
                  Save task
                </Btn>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
